import { useState, useEffect } from 'react';
import ListingsDisplay from './ListingsDisplay';
import { 
  Flex,
  View,
  Text,
  Heading,
  Button,
  Card,
  Badge,
  Loader,
  Alert
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession, getCurrentUser} from 'aws-amplify/auth';
import awsConfig from '../aws-exports';
import JobDetailsModal from './JobDetailsModal';

const InternshipListings = () => {
  const [keywords, setKeywords] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // New states for full description
  const [loadingFullDescription, setLoadingFullDescription] = useState(false);
  const [fullDescriptionError, setFullDescriptionError] = useState(null);

  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [savedListingTitle, setSavedListingTitle] = useState('');
  const [showSavedListings, setShowSavedListings] = useState(false);

  // Compatibility states
  const [showCompatibility, setShowCompatibility] = useState(false);
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);
  const [compatibilityError, setCompatibilityError] = useState(null);

  const API_NAME = 'ai_analysis';

  // NEW: Save functionality states
  const [currentUserId, setCurrentUserId] = useState(null);
  const [savedListings, setSavedListings] = useState({});
  const [compatMap, setCompatMap] = useState({});

  const getUserStorageKey = (base) => (currentUserId ? `${base}_${currentUserId}` : base);

  // Get current user ID on mount
  useEffect(() => {
    getCurrentUser().then(u => setCurrentUserId(u.userId)).catch(() => setCurrentUserId(null));
  }, []);


  // Save search state
  useEffect(() => {
    if (!currentUserId) return;
    const searchState = {
      keywords,
      sortBy,
      hasSearched,
      internships,
      currentPage
    };
    sessionStorage.setItem(`internshipSearchState_${currentUserId}`, JSON.stringify(searchState));
  }, [keywords, sortBy, hasSearched, internships, currentPage, currentUserId]);

  // Restore search state on mount
  useEffect(() => {
    if (!currentUserId) return;
    const savedState = sessionStorage.getItem(`internshipSearchState_${currentUserId}`);
    if (savedState) {
      const state = JSON.parse(savedState);
      setKeywords(state.keywords || '');
      setSortBy(state.sortBy || 'relevance');
      setHasSearched(state.hasSearched || false);
      setInternships(state.internships || []);
      setCurrentPage(state.currentPage || 1);
    }
  }, [currentUserId]);

  // Save/unsave listing functions
  const isSaved = (listingId) => !!savedListings[listingId];
    
  // Add a new state for saving status at the top of your component
  const [isSavingJob, setIsSavingJob] = useState(false);

  // Updated toggleSaveListing function with loading state
  const toggleSaveListing = async (listing) => {
    if (!currentUserId) return;
    
    setIsSavingJob(true); // Start loading
    
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const saveJobApi = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'save_job'
      );
      
      if (isSaved(listing.id)) {
        // Delete the job
        const deleteApi = awsConfig.aws_cloud_logic_custom.find(
          api => api.name === 'delete_saved_job'
        );
        
        const response = await fetch(`${deleteApi.endpoint}/delete-job`, {
          method: 'POST',
          headers: {
            'Authorization': idToken.toString(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ jobId: listing.id })
        });
        
        if (response.ok) {
          // Update local state
          setSavedListings(prev => {
            const updated = {...prev};
            delete updated[listing.id];
            localStorage.setItem(getUserStorageKey('saved_listings'), JSON.stringify(updated));
            return updated;
          });
          
          // Also remove from compat map if it exists
          setCompatMap(prev => {
            const updated = {...prev};
            delete updated[listing.id];
            return updated;
          });
        }
      } else {
        // Check if we have compatibility data for this specific listing
        const hasCompatibilityData = compatibilityResult && selectedInternship?.id === listing.id;
        
        // Build the request body
        const requestBody = {
          jobId: listing.id,
          jobTitle: listing.title,
          company: listing.company,
          location: listing.location,
          description: listing.fullDescription || listing.description || '',
          requirements: Array.isArray(listing.requirements) ? listing.requirements : [],
          url: listing.apply_url || '',
          source: listing.source || '',
          salary: listing.salary || '',
          jobType: listing.jobType || '',
          experienceLevel: 'Internship/Entry Level',
          skills: []
        };

        // Add compatibility data if it exists
        if (hasCompatibilityData) {
          requestBody.score = compatibilityResult.score || 0;
          requestBody.scoreDetails = {
            explanation: compatibilityResult.explanation || '',
            keyMatches: compatibilityResult.key_matches || [],
            missingSkills: compatibilityResult.missing_skills || [],
            recommendations: compatibilityResult.recommendations || []
          };
        }

        const response = await fetch(`${saveJobApi.endpoint}/save-job`, {
          method: 'POST',
          headers: {
            'Authorization': idToken.toString(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          // Update local state IMMEDIATELY
          const savedListing = {
            ...listing,
            savedAt: new Date().toISOString(),
            score: hasCompatibilityData ? compatibilityResult.score : undefined
          };
          
          setSavedListings(prev => {
            const updated = {
              ...prev,
              [listing.id]: savedListing
            };
            // Also update localStorage
            localStorage.setItem(getUserStorageKey('saved_listings'), JSON.stringify(updated));
            window.dispatchEvent(new Event('saved-listings-updated'));
            return updated;
          });
          
          // If we saved with compatibility data, also update the compat map
          if (hasCompatibilityData) {
            saveCompatEntry(listing, compatibilityResult.score, compatibilityResult);
          }
          
          // Show notifications
          setSavedListingTitle(listing.title);
          setShowSaveNotification(true);
          setShowSavedListings(true);
          setTimeout(() => setShowSaveNotification(false), 3000);
        }
      }
    } catch (error) {
      console.error('Error saving/deleting job:', error);
      setError('Failed to save/delete job. Please try again.');
    } finally {
      setIsSavingJob(false); // Stop loading
    }
  };

  // Add this function to fetch saved jobs
  const fetchSavedJobs = async () => {
    console.log('1. fetchSavedJobs started');
    if (!currentUserId) return;
    
    try {
      console.log('2. Getting session...');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        console.log('No token, exiting');
        return;
      }
      console.log('3. Got token');

      const getSavedJobsApi = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'get_saved_job'
      );
      console.log('4. API config found');
      
      const url = `${getSavedJobsApi.endpoint}/saved-job?limit=100`;
      console.log('5. URL:', url);
      
      console.log('6. Making fetch...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        }
      });
      console.log('7. Fetch complete, status:', response.status);
      
      if (response.ok) {
        console.log('8. Response OK, parsing JSON...');
        const data = await response.json();
        console.log('9. Data received:', data);
        
        // Check if data structure is what we expect
        if (!data.jobs) {
          console.log('WARNING: No jobs property in response. Full response:', data);
        }
        
        const savedMap = {};
        if (data.jobs && Array.isArray(data.jobs)) {
          console.log('10. Processing', data.jobs.length, 'jobs');
          data.jobs.forEach(job => {
            // Extract from jobDetails if it exists
            const details = job.jobDetails || {};
            
            savedMap[job.jobId] = {
              // Core identifiers
              id: job.jobId,
              title: details.title || job.title || 'Untitled',
              company: details.company || job.company || '',
              location: details.location || job.location || '',
              locationType: details.locationType || 'On-site',
              salary: details.salary || job.salary || 'Not specified',
              
              // Important fields for modal
              description: details.description || 'No description available',
              requirements: details.requirements || [],
              apply_url: details.url || job.url || '',  // This fixes the "Missing redirect URL" error
              source: details.source || job.source || 'Saved',
              industry: details.industry || details.category || 'General',
              jobType: details.jobType || job.jobType || 'Full-time',
              
              // Salary details if available
              salaryMin: details.salaryMin || 0,
              salaryMax: details.salaryMax || 0,
              salaryPeriod: details.salaryPeriod || 'annum',
              
              // Dates and scores
              postedDate: job.postedDate || job.savedAt,
              postedDays: 0,
              score: job.score || 0,
              savedAt: job.savedAt,
              matchScore: job.score || 0,
              
              // Store the full details for reference
              fullDescription: details.description || '',
              scoreDetails: job.scoreDetails || null, 
              // Include raw data for debugging
              raw: job
            };
          });
        }
        
        console.log('11. Setting state with', Object.keys(savedMap).length, 'jobs');
        setSavedListings(savedMap);
        
        if (Object.keys(savedMap).length > 0) {
          setShowSavedListings(true);
        }
        
        // Also update compat scores if they exist
        const scoreMap = {};
        if (data.jobs && Array.isArray(data.jobs)) {
          data.jobs.forEach(job => {
            if (job.score > 0) {
              scoreMap[job.jobId] = {
                score: job.score,
                explanation: job.scoreHighlights?.recommendation || ''
              };
            }
          });
        }
        setCompatMap(scoreMap);
        
      } else {
        console.log('ERROR: Response not OK');
        const errorText = await response.text();
        console.log('Error details:', errorText);
      }
    } catch (error) {
      console.error('CATCH: Error in fetchSavedJobs:', error.message);
    }
    console.log('12. fetchSavedJobs finished');
  };

  // Update the useEffect to call this
  useEffect(() => {
    if (currentUserId) {
      fetchSavedJobs();
    }
    // fetchSavedJobs is component-scoped; intentionally re-runs only on user change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Persist compat score after a successful check
  const saveCompatEntry = (listing, score, parsedPayload) => {
    if (!currentUserId) return;
    const key = getUserStorageKey('compat_scores');
    let map = {};
    try { map = JSON.parse(localStorage.getItem(key) || '{}'); } catch {}
    map[listing.id] = {
      id: listing.id,
      title: listing.title,
      company: listing.company,
      location: listing.location,
      score: typeof score === 'number' ? score : 0,
      explanation: parsedPayload?.explanation || '',
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(map));
    setCompatMap(map);
    window.dispatchEvent(new Event('compatibility-updated'));
  };

const fetchInternships = async (searchKeywords = '') => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'get_internship_data'
      );

      if (!apiConfig) {
        throw new Error('API get_internship_data not found in aws-exports configuration');
      }

      const apiEndpoint = `${apiConfig.endpoint}/get_internship_data`;
      
      const queryParams = new URLSearchParams();
      
      // Send the full search string to the API
      if (searchKeywords) {
        queryParams.append('q', searchKeywords.trim());
      }
      
      queryParams.append('employment_type', 'internship');
      queryParams.append('limit', '100');
      
      const url = `${apiEndpoint}?${queryParams.toString()}`;
      
      console.log('Fetching internships with URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Request failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      
      if (responseData.success && responseData.results && responseData.results.jobs) {
        let transformedInternships = responseData.results.jobs.map((job, index) => ({
          id: job.jobId,
          title: job.title,
          company: job.company,
          location: job.location.city || job.location.display,
          locationType: job.is_remote ? 'Remote' : 'On-site',
          salary: job.salary.display || `£${job.salary.min || 'N/A'}-${job.salary.max || 'N/A'}/${job.salary.period || 'hour'}`,
          salaryMin: job.salary.min || 0,
          salaryMax: job.salary.max || 0,
          salaryPeriod: job.salary.period || 'annum',
          matchScore: Math.floor(Math.random() * 40) + 60,
          postedDate: job.posted_date,
          postedDays: calculateDaysAgo(job.posted_date),
          industry: job.category || 'General',
          jobType: job.job_type || 'Full-time',
          description: job.description,
          requirements: job.requirements,
          apply_url: job.apply_url,
          source: job.source,
          raw: job
        }));


        // Filter out jobs older than 31 days (1 month)
        const maxDaysOld = 31; // 1 month limit
        const beforeFilterCount = transformedInternships.length;

        // First, let's see how many jobs would be filtered out
        const recentJobs = transformedInternships.filter(internship => {
          if (!internship.postedDate) return true;
          return internship.postedDays <= maxDaysOld;
        });

        const oldJobsCount = beforeFilterCount - recentJobs.length;

        // Only apply the filter if we'll have at least some results left
        if (recentJobs.length >= 5) {
          transformedInternships = recentJobs;
          console.log(`Filtered out ${oldJobsCount} jobs older than ${maxDaysOld} days`);
        } else {
          // If filtering would leave too few results, keep more jobs but warn the user
          console.log(`Warning: Only ${recentJobs.length} jobs from last ${maxDaysOld} days. Showing older results as well.`);
          
          // Sort by date so newer jobs appear first
          transformedInternships.sort((a, b) => {
            if (!a.postedDays && !b.postedDays) return 0;
            if (!a.postedDays) return 1;
            if (!b.postedDays) return -1;
            return a.postedDays - b.postedDays;
          });
          
          // You could also limit to slightly older (e.g., 60 days) as a compromise
          const compromiseMaxDays = 60;
          transformedInternships = transformedInternships.filter(internship => {
            if (!internship.postedDate) return true;
            return internship.postedDays <= compromiseMaxDays;
          });
        }

        // Warn if many results were old
        if (oldJobsCount > beforeFilterCount * 0.5) {
          console.warn(`Note: ${oldJobsCount} out of ${beforeFilterCount} jobs were older than 31 days and filtered out`);
        }

        // FILTER RESULTS TO MATCH ALL KEYWORDS
        if (searchKeywords && searchKeywords.trim()) {
          // Split search keywords by comma or space and clean them up
          const keywords = searchKeywords
            .toLowerCase()
            .split(/[,\s]+/)
            .filter(keyword => keyword.length > 2); // Ignore very short words
          
          console.log('Filtering with keywords:', keywords);
          
          // Filter internships - must match ALL keywords
          transformedInternships = transformedInternships.filter(internship => {
            // Create searchable text from all relevant fields
            const searchableText = `
              ${internship.title} 
              ${internship.company} 
              ${internship.location} 
              ${internship.industry} 
              ${internship.jobType}
              ${internship.description || ''}
              ${internship.requirements ? internship.requirements.join(' ') : ''}
            `.toLowerCase();
            
            // Check if ALL keywords are present in the searchable text
            const matchesAllKeywords = keywords.every(keyword => 
              searchableText.includes(keyword)
            );
            
            // Log matching details for debugging
            if (matchesAllKeywords) {
              console.log(`✓ Matched: "${internship.title}" at ${internship.location}`);
            }
            
            return matchesAllKeywords;
          });
          
          console.log(`Filtered from ${responseData.results.jobs.length} to ${transformedInternships.length} internships`);
        }

        setInternships(transformedInternships);
        setHasSearched(true);
        
        if (transformedInternships.length === 0 && searchKeywords) {
          // If no results after filtering, show a helpful message
          console.log('No internships matched all keywords:', searchKeywords);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching internships:', err);
      if (err.message?.includes('Unauthorized') || err.message?.includes('401')) {
        setError('Authentication failed. Please log in again.');
      } else if (err.message?.includes('Network')) {
        setError('Network error. Please check your connection.');
      } else {
        setError('Failed to load internships. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFullJobDescription = async (internship) => {
    try {
      setLoadingFullDescription(true);
      setFullDescriptionError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'job_details'
      );
      
      if (!apiConfig) {
        throw new Error('Job details API not configured');
      }

      const apiEndpoint = `${apiConfig.endpoint}/job-details`;
      
      const requestBody = {
        source: internship.source,
        jobId: internship.id
      };
      
      if (internship.source === 'adzuna-api') {
        const adzunaJobId = internship.id.replace('adzuna_', '');
        const properAdzunaUrl = `https://www.adzuna.co.uk/jobs/details/${adzunaJobId}`;
        requestBody.redirectUrl = properAdzunaUrl;
        requestBody.job = internship.raw;
      }
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to fetch full job description');
      }

      const result = await response.json();
      
      if (result.success && result.job) {
        const updatedInternship = {
          ...internship,
          description: result.job.fullDescription || result.job.description,
          fullDescription: result.job.fullDescription || result.job.description,
          descriptionEnhanced: true
        };
        
        setSelectedInternship(updatedInternship);
      }
      
    } catch (error) {
      console.error('Error fetching full job description:', error);
      setFullDescriptionError(error.message || 'Failed to load full job description');
    } finally {
      setLoadingFullDescription(false);
    }
  };

  const calculateDaysAgo = (dateString) => {
    if (!dateString) return 0;
    const postedDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - postedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const sortedInternships = [...internships].sort((a, b) => {
    if (sortBy === 'relevance') {
      return b.matchScore - a.matchScore;
    } else if (sortBy === 'recent') {
      return a.postedDays - b.postedDays;
    } else if (sortBy === 'salary') {
      return b.salaryMin - a.salaryMin;
    }
    return 0;
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  useEffect(() => {
    setCurrentPage(1);
  }, [keywords]);

  const handleSearch = () => {
    if (!keywords.trim()) {
      setError('Please enter search keywords (e.g., "Manchester, Computing")');
      return;
    }
    
    fetchInternships(keywords);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setKeywords('');
    setInternships([]);
    setHasSearched(false);
    setError(null);
  };

  const handleApply = (internship) => {
    if (internship.apply_url) {
      window.open(internship.apply_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCheckCompatibility = async (internship) => {
    try {
      setIsCheckingCompatibility(true);
      setCompatibilityError(null);
      setShowCompatibility(true);
      
      const currentUserIdLocal = await getCurrentUser().then(user => user.userId);
      const savedFeedback = localStorage.getItem(`cv_latest_feedback_${currentUserIdLocal}`);
      
      if (!savedFeedback) {
        setCompatibilityError('No CV uploaded. Please upload your CV in the CV Analysis section first.');
        setIsCheckingCompatibility(false);
        return;
      }
      
      const cvData = JSON.parse(savedFeedback);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const identityId = session.identityId;
      
      if (!idToken || !identityId) {
        throw new Error('No authentication token or identity available');
      }
      
      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'ai_analysis'
      );
      
      if (!apiConfig) {
        throw new Error('AI analysis API not configured');
      }
      
      const s3Uri = localStorage.getItem(`cv_latest_s3uri_${currentUserIdLocal}`);
      if (!s3Uri) {
        throw new Error('CV file location not found');
      }
      
      const compatibilityPrompt = `You are an expert recruitment consultant and career adviser based in the UK, specialising in matching candidates with suitable positions. Analyse the provided CV content against the job listing details to determine compatibility and suitability.
      
  Job Details:
  - Title: {jobTitle}
  - Industry: {jobIndustry}
  - Description: {jobDescription}
  - Requirements: {jobRequirements}

  CV Information:
  - Filename: {cvFileName}
  - CV Score: {cvScore}

  Provide a thorough compatibility analysis focusing on:

  1. SKILLS ALIGNMENT: Compare the candidate's technical and soft skills against the job requirements. Identify exact matches, transferable skills, and any critical gaps.

  2. EXPERIENCE RELEVANCE: Assess how the candidate's work history, achievements, and responsibilities align with what the employer is seeking.

  3. QUALIFICATIONS MATCH: Evaluate educational background, certifications, and professional development against the role's requirements.

  4. CULTURAL FIT INDICATORS: Based on the CV's tone, structure, and content, assess potential alignment with the company culture implied in the job listing.

  IMPORTANT: Use British English spelling throughout (e.g., analyse not analyze, specialising not specializing, colour not color, centre not center, programme not program).

  Provide your response in EXACTLY this JSON format:
  {
    "compatibility_score": [0-100 representing overall match percentage],
    "explanation": "[2-3 sentences providing a concise overview of the candidate's suitability for this role, highlighting the most significant strengths and any major concerns]",
    "key_matches": [
      "[Specific skill, experience, or qualification from CV that directly matches job requirements]",
      "[Another strong match with specific examples]",
      "[Additional match - be specific and reference actual content]"
    ],
    "missing_skills": [
      "[Critical requirement from job listing not evident in CV]",
      "[Another gap that could impact application success]",
      "[Additional missing element if applicable]"
    ],
    "recommendations": [
      "[Specific advice on whether to apply and how to strengthen application]",
      "[Suggestion for addressing any gaps or enhancing CV for this role]",
      "[Additional recommendation for maximising success]"
    ]
  }

  Be specific, constructive, and honest in your assessment. Reference actual content from both the CV and job listing. Consider entry-level/internship expectations appropriately - don't penalise lack of extensive experience for junior roles. Focus on potential, transferable skills, and learning ability for such positions.`;

      const formattedPrompt = compatibilityPrompt
        .replace('{jobTitle}', internship.title)
        .replace('{jobIndustry}', internship.industry)
        .replace('{jobDescription}', internship.description)
        .replace('{jobRequirements}', internship.requirements ? internship.requirements.join(', ') : 'Not specified')
        .replace('{cvFileName}', cvData.fileName)
        .replace('{cvScore}', cvData.score);

      const apiEndpoint = `${apiConfig.endpoint}/${API_NAME}`;
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          s3Uri: s3Uri,
          fileName: cvData.fileName,
          identityId: identityId,
          systemPrompt: formattedPrompt,
          jobDetails: {
            jobId: internship.id,
            title: internship.title,
            company: internship.company,
            location: internship.location,
            description: internship.fullDescription || internship.description || '',
            requirements: Array.isArray(internship.requirements) ? internship.requirements : [],
            url: internship.apply_url || '',
            source: internship.source || '',
            salary: internship.salary || '',
            jobType: internship.jobType || '',
            experienceLevel: 'Internship/Entry Level',
            skills: []
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to check compatibility');
      }
      
      const result = await response.json();
      
      let compatibilityData;
      try {
        const parsedResult = JSON.parse(result.translated);
        compatibilityData = {
          score: parsedResult.compatibility_score || 0,
          explanation: parsedResult.explanation || 'Unable to determine compatibility.',
          key_matches: parsedResult.key_matches || [],
          missing_skills: parsedResult.missing_skills || [],
          recommendations: parsedResult.recommendations || []
        };
      } catch (parseError) {
        console.error('Error parsing compatibility result:', parseError);
        throw new Error('Invalid response format from compatibility analysis');
      }
      
      setCompatibilityResult(compatibilityData);
      saveCompatEntry(internship, compatibilityData.score, compatibilityData);
      
      // If this job is already saved, update it with the score
      if (isSaved(internship.id)) {
        console.log('Updating saved job with compatibility score...');
        
        try {
          const saveJobApi = awsConfig.aws_cloud_logic_custom.find(
            api => api.name === 'save_job'
          );
          
          // Prepare the update request with all job details plus the new score
          const updateBody = {
            jobId: internship.id,
            jobTitle: internship.title,
            company: internship.company,
            location: internship.location,
            description: internship.fullDescription || internship.description || '',
            requirements: Array.isArray(internship.requirements) ? internship.requirements : [],
            url: internship.apply_url || '',
            source: internship.source || '',
            salary: internship.salary || '',
            jobType: internship.jobType || '',
            experienceLevel: 'Internship/Entry Level',
            skills: [],
            score: compatibilityData.score,
            scoreDetails: {
              explanation: compatibilityData.explanation || '',
              keyMatches: compatibilityData.key_matches || [],
              missingSkills: compatibilityData.missing_skills || [],
              recommendations: compatibilityData.recommendations || []
            }
          };
          
          const updateResponse = await fetch(`${saveJobApi.endpoint}/save-job`, {
            method: 'POST',
            headers: {
              'Authorization': idToken.toString(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateBody)
          });
          
          if (updateResponse.ok) {
            console.log('Saved job updated with score successfully');
            
            // Update local state to reflect the score immediately
            setSavedListings(prev => ({
              ...prev,
              [internship.id]: {
                ...prev[internship.id],
                score: compatibilityData.score,
                scoreDetails: compatibilityData
              }
            }));
            
            // Also update the compat map
            setCompatMap(prev => ({
              ...prev,
              [internship.id]: {
                score: compatibilityData.score,
                explanation: compatibilityData.explanation
              }
            }));
          } else {
            console.error('Failed to update saved job with score');
          }
        } catch (updateError) {
          console.error('Error updating saved job:', updateError);
          // Don't throw - the scoring worked, just the update failed
        }
      }
      
    } catch (error) {
      console.error('Error checking compatibility:', error);
      setCompatibilityError(error.message || 'Failed to check compatibility. Please try again.');
    } finally {
      setIsCheckingCompatibility(false);
    }
  };

  const handleInternshipClick = async (internship) => {
    setSelectedInternship(internship);
    setShowModal(true);
 
     // If this saved job has a score, set the compatibility result
    if (internship.score && internship.scoreDetails) {
      setCompatibilityResult({
        score: internship.score,
        explanation: internship.scoreDetails.explanation || '',
        key_matches: internship.scoreDetails.keyMatches || [],
        missing_skills: internship.scoreDetails.missingSkills || [],
        recommendations: internship.scoreDetails.recommendations || []
      });
      setShowCompatibility(true);  // Show the compatibility panel immediately
    } else {
      setCompatibilityResult(null);
      setShowCompatibility(false);
    }

    await fetchFullJobDescription(internship);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInternship(null);
    setShowCompatibility(false);
    setCompatibilityResult(null);
    setCompatibilityError(null);
    setLoadingFullDescription(false);
    setFullDescriptionError(null);
  };

  const handleRetry = () => {
    if (keywords.trim()) {
      fetchInternships(keywords);
    }
  };

  return (
    <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
      {showModal && selectedInternship && (
        <JobDetailsModal
          readOnly={false} 
          showModal={showModal}
          job={selectedInternship}
          onClose={closeModal}
          onApply={handleApply}
          onSave={toggleSaveListing}
          onCheckCompatibility={handleCheckCompatibility}
          isSaved={isSaved(selectedInternship?.id)}
          isSavingJob={isSavingJob}
          compatibilityResult={compatibilityResult}
          showCompatibility={showCompatibility}
          isCheckingCompatibility={isCheckingCompatibility}
          compatibilityError={compatibilityError}
          loadingFullDescription={loadingFullDescription}
          fullDescriptionError={fullDescriptionError}
        />
      )}
      
      <style>{`
        /* Compatibility circle chip on listing cards */
        .compat-chip{
          width:64px; 
          height:64px; 
          pointer-events:none;
          margin-right: 0.75rem;
        }
        .compat-ring{
          width:64px; 
          height:64px; 
          border-radius:50%; 
          display:flex; 
          align-items:center; 
          justify-content:center;
        }
        .compat-inner{
          width:52px; 
          height:52px; 
          border-radius:50%;
          background:rgba(20,22,28,0.9); 
          border:1px solid rgba(255,255,255,.15);
          display:flex; 
          align-items:center; 
          justify-content:center; 
          color:#fff; 
          font-weight:800; 
          font-size:1.1rem;
        }

        /* Job description HTML rendering styles */
        .job-description p {
          margin-bottom: 1rem;
          color: rgba(255,255,255,0.8);
        }

        .job-description strong {
          color: white;
          font-weight: 600;
        }

        .job-description ul, 
        .job-description ol {
          margin: 1rem 0;
          padding-left: 1.5rem;
          color: rgba(255,255,255,0.8);
        }

        .job-description li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }

        .job-description br {
          display: block;
          margin-bottom: 0.5rem;
        }

        .job-description h1,
        .job-description h2,
        .job-description h3,
        .job-description h4,
        .job-description h5,
        .job-description h6 {
          color: white;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .job-description h1 { font-size: 1.5rem; }
        .job-description h2 { font-size: 1.3rem; }
        .job-description h3 { font-size: 1.1rem; }
        .job-description h4 { font-size: 1rem; }
        .job-description h5 { font-size: 0.9rem; }
        .job-description h6 { font-size: 0.85rem; }

        .job-description a {
          color: #667eea;
          text-decoration: underline;
        }

        .job-description a:hover {
          color: #764ba2;
        }

        .job-description blockquote {
          border-left: 3px solid rgba(102, 126, 234, 0.5);
          padding-left: 1rem;
          margin: 1rem 0;
          color: rgba(255,255,255,0.7);
          font-style: italic;
        }

        .job-description code {
          background: rgba(102, 126, 234, 0.2);
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.9em;
          color: rgba(255,255,255,0.9);
        }

        .job-description pre {
          background: rgba(30, 35, 45, 0.5);
          padding: 1rem;
          border-radius: 6px;
          overflow-x: auto;
          margin: 1rem 0;
        }

        .job-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }

        .job-description th,
        .job-description td {
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.5rem;
          text-align: left;
        }

        .job-description th {
          background: rgba(102, 126, 234, 0.2);
          color: white;
          font-weight: 600;
        }

        .job-description hr {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.2);
          margin: 1.5rem 0;
        }

        /* Input placeholder styles */
        input::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
          font-style: italic;
        }
        
        input:focus::placeholder {
          color: rgba(255, 255, 255, 0.3) !important;
        }
      `}</style>
      
      <View
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '-150px',
            right: '-150px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
      </View>

      <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
        {loading && (
          <Card 
            borderRadius="12px" 
            padding="3rem"
            style={{
              background: 'rgba(30, 35, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              textAlign: 'center',
              maxWidth: '500px',
              margin: '0 auto'
            }}
          >
            <Flex direction="column" alignItems="center" gap="1rem">
              <Loader size="large" />
              <Text fontSize="1.1rem" color="rgba(255,255,255,0.8)">Loading internships...</Text>
            </Flex>
          </Card>
        )}

        {error && !loading && (
          <Card 
            borderRadius="12px" 
            padding="2rem"
            style={{
              background: 'rgba(30, 35, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              maxWidth: '500px',
              margin: '0 auto'
            }}
          >
            <Alert 
              variation="error"
              style={{ borderRadius: '8px', marginBottom: '1rem' }}
            >
              {error}
            </Alert>
            <Flex justifyContent="center">
              <Button 
                onClick={handleRetry}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                Retry
              </Button>
            </Flex>
          </Card>
        )}

        {!loading && (
          <>
            <Card 
              borderRadius="12px" 
              padding="2rem" 
              marginBottom="2rem"
              style={{
                background: 'rgba(30, 35, 45, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
              }}
            >
              <Heading level={3} marginBottom="1.5rem" style={{ color: 'white', textAlign: 'center' }}>
                Search Internships & Junior Roles
              </Heading>
              
              <Flex direction="column" gap="1rem" alignItems="center">
                <View style={{ width: '100%', maxWidth: '900px' }}>
                  <View style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder='e.g., "Manchester, Computing" or "Marketing London"'
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      onKeyPress={handleKeyPress}
                      style={{ 
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        border: '2px solid rgba(102, 126, 234, 0.3)',
                        color: 'white',
                        flex: 1,
                        height: '48px',
                        fontSize: '1rem',
                        padding: '0 1.25rem',
                        outline: 'none',
                        transition: 'all 0.3s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.6)';
                        e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                        e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                      }}
                    />
                    <button
                      onClick={handleSearch}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '0 2.5rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        cursor: 'pointer',
                        height: '48px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      Search
                    </button>
                    
                    {/* Saved Listings Toggle Button - Next to Search */}
                    {Object.keys(savedListings).length > 0 && (
                      <button
                        onClick={() => setShowSavedListings(!showSavedListings)}
                        style={{
                          background: showSavedListings 
                            ? 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)' 
                            : 'rgba(46, 204, 113, 0.2)',
                          border: '1px solid rgba(46, 204, 113, 0.4)',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '0 1.5rem',
                          fontSize: '1rem',
                          fontWeight: '600',
                          height: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          whiteSpace: 'nowrap',
                          boxShadow: showSavedListings ? '0 4px 15px rgba(46, 204, 113, 0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 20px rgba(46, 204, 113, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = showSavedListings ? '0 4px 15px rgba(46, 204, 113, 0.3)' : 'none';
                        }}
                      >
                        <svg 
                          width="16" 
                          height="20" 
                          viewBox="0 0 18 22" 
                          fill={showSavedListings ? 'white' : 'none'}
                          stroke="white"
                          strokeWidth="2"
                        >
                          <path d="M1 3v18l8-5 8 5V3c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2z"/>
                        </svg>
                        {showSavedListings ? 'Hide Saved' : 'Show Saved'} ({Object.keys(savedListings).length})
                      </button>
                    )}
                  </View>
                  
                  {/* Clear Search button below if searched */}
                  {hasSearched && (
                    <Flex justifyContent="center" marginTop="1rem">
                      <Button
                        onClick={clearSearch}
                        size="small"
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: 'white',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Clear Search
                      </Button>
                    </Flex>
                  )}
                </View>
              </Flex>
            </Card>

            {/* Save Notification Popup */}
            {showSaveNotification && (
              <View
                style={{
                  position: 'fixed',
                  top: '100px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10000,
                  animation: 'slideDown 0.3s ease-out'
                }}
              >
                <Card
                  padding="1rem 1.5rem"
                  style={{
                    background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 30px rgba(46, 204, 113, 0.4)',
                    minWidth: '300px',
                    maxWidth: '500px'
                  }}
                >
                  <Flex alignItems="center" gap="1rem">
                    <View
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg 
                        width="20" 
                        height="24" 
                        viewBox="0 0 18 22" 
                        fill="white"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <path d="M1 3v18l8-5 8 5V3c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2z"/>
                      </svg>
                    </View>
                    <View flex="1">
                      <Text color="white" fontWeight="bold" fontSize="1rem">
                        Listing Saved!
                      </Text>
                      <Text color="rgba(255,255,255,0.9)" fontSize="0.85rem">
                        "{savedListingTitle}" has been added to your saved listings
                      </Text>
                    </View>
                    <button
                      onClick={() => setShowSaveNotification(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        padding: '0',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </Flex>
                </Card>
              </View>
            )}

            {/* Processing Indicator - Add this right after the Save Notification Popup */}
            {isSavingJob && (
              <View
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10001,
                  backgroundColor: 'rgba(30, 35, 45, 0.95)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(102, 126, 234, 0.5)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  minWidth: '250px',
                  textAlign: 'center'
                }}
              >
                <Flex direction="column" alignItems="center" gap="1rem">
                  <Loader size="large" />
                  <Text color="white" fontSize="1.1rem" fontWeight="600">
                    Processing...
                  </Text>
                  <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                    Saving job details
                  </Text>
                </Flex>
              </View>
            )}

           {!hasSearched && !loading && !error && (
             <Card 
               borderRadius="12px" 
               padding="3rem"
               textAlign="center"
               marginBottom="2rem"
               style={{
                 background: 'rgba(30, 35, 45, 0.85)',
                 backdropFilter: 'blur(20px)',
                 border: '1px solid rgba(255,255,255,0.1)',
                 boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
               }}
             >
               <Heading level={3} marginBottom="1rem" style={{ color: 'white' }}>
                 Welcome to the Internship Search
               </Heading>
               <Text color="rgba(255,255,255,0.7)" fontSize="1.1rem">
                 Search for internships and junior roles by entering keywords above.
                 Try searching for locations, job types, or skills (e.g., "Manchester Computing" or "London Marketing").
               </Text>
             </Card>
           )}

            {/* Saved Listings Section - Full-size cards with See Details */}
            {showSavedListings && Object.keys(savedListings).length > 0 && (
              <Card 
                borderRadius="12px" 
                padding="2rem" 
                marginBottom="2rem"
                style={{
                  background: 'rgba(30, 35, 45, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(46, 204, 113, 0.3)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <Flex justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
                  <Heading level={3} style={{ color: 'white' }}>
                    Saved Listings ({Object.keys(savedListings).length})
                  </Heading>
                </Flex>

                <Flex direction="column" gap="1rem">
                  {Object.values(savedListings).sort((a, b) => b.timestamp - a.timestamp).map((listing) => {
                    const compatScore = compatMap[listing.id]?.score;
                    const ringColor = typeof compatScore === 'number'
                      ? (compatScore >= 80 ? '#2ECC71' : compatScore >= 60 ? '#F39C12' : compatScore >= 40 ? '#E67E22' : '#E74C3C')
                      : null;

                    // Create a full internship object for the modal
                    const fullListing = {
                      ...listing,
                      postedDays: listing.postedDays || 0,
                      industry: listing.industry || 'General',
                      jobType: listing.jobType || 'Full-time',
                      source: listing.source || 'Saved',
                      description: listing.description || 'No description available',
                      requirements: listing.requirements || []
                    };

                    return (
                      <Card 
                        key={listing.id} 
                        borderRadius="12px" 
                        padding="1.5rem"
                        style={{
                          background: 'rgba(30, 35, 45, 0.85)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(46, 204, 113, 0.2)',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                          transition: 'all 0.3s',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)';
                        }}
                      >
                        {/* Remove from saved button */}
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            toggleSaveListing(listing);
                            // Hide saved listings section if no more saved items
                            if (Object.keys(savedListings).length === 1) {
                              setShowSavedListings(false);
                            }
                          }}
                          title="Remove from saved"
                          aria-label="Remove from saved listings"
                          style={{
                            position:'absolute',
                            left:12,
                            top:12,
                            width:36,
                            height:36,
                            borderRadius:8,
                            border:'2px solid #E74C3C',
                            background: 'rgba(231, 76, 60, 0.15)',
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            cursor:'pointer',
                            zIndex:2,
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(231, 76, 60, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(231, 76, 60, 0.15)';
                          }}
                        >
                          <span style={{ color: '#E74C3C', fontSize: '1.2rem', lineHeight: '1' }}>×</span>
                        </button>
                        
                        <Flex alignItems="center" gap="1.5rem">
                          <View
                            backgroundColor="rgba(46, 204, 113, 0.1)"
                            width="80px"
                            height="80px"
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink="0"
                            border="1px solid rgba(46, 204, 113, 0.3)"
                          >
                            <Text color="rgba(46, 204, 113, 0.8)" fontSize="0.875rem" fontWeight="bold">
                              {listing.company.substring(0, 3).toUpperCase()}
                            </Text>
                          </View>

                          <Flex direction="column" gap="0.5rem" flex="1">
                            <Heading level={4} fontSize="1.25rem" style={{ color: 'white', paddingLeft: '2.75rem' }}>
                              {listing.title}
                            </Heading>
                            <Text fontSize="1rem" color="rgba(255,255,255,0.9)" style={{ paddingLeft: '2.75rem' }}>
                              {listing.company}
                            </Text>
                            <Flex gap="0.5rem" alignItems="center" flexWrap="wrap" style={{ paddingLeft: '2.75rem' }}>
                              <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                                {listing.location} ({listing.locationType})
                              </Text>
                              <Text color="rgba(255,255,255,0.5)">•</Text>
                              <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                                {listing.salary}
                              </Text>
                              <Text color="rgba(255,255,255,0.5)">•</Text>
                              <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem">
                                {(() => {
                                  // Extract source from id field
                                  if (listing.id) {
                                    const match = listing.id.match(/^([a-zA-Z]+)[_-]/);
                                    if (match) {
                                      const source = match[1];
                                      return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase();
                                    }
                                  }
                                  return 'Direct';
                                })()}
                              </Text>


                              <Badge 
                                size="small"
                                style={{
                                  backgroundColor: 'rgba(46, 204, 113, 0.2)',
                                  color: '#2ECC71',
                                  border: '1px solid rgba(46, 204, 113, 0.3)',
                                  fontSize: '0.7rem'
                                }}
                              >
                                SAVED
                              </Badge>
                            </Flex>
                          </Flex>

                          {/* Compatibility circle chip if score exists */}
                          {typeof compatScore === 'number' && (
                            <div className="compat-chip">
                              <div className="compat-ring" style={{ background: `conic-gradient(${ringColor} ${compatScore * 3.6}deg, rgba(255,255,255,0.12) 0deg)` }}>
                                <div className="compat-inner"><span>{compatScore}</span></div>
                              </div>
                            </div>
                          )}

                          <Button
                            variation="primary"
                            size="large"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInternshipClick(fullListing);
                            }}
                            style={{
                              background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                              border: 'none',
                              minWidth: '120px',
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: 'white',
                              borderRadius: '8px',
                              transition: 'all 0.3s',
                              boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 20px rgba(46, 204, 113, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 15px rgba(46, 204, 113, 0.3)';
                            }}
                          >
                            See Details
                          </Button>
                        </Flex>
                      </Card>
                    );
                  })}
                </Flex>
              </Card>
            )}    

            {hasSearched && !error && (
              <ListingsDisplay
                variant="full"
                sortedInternships={sortedInternships}
                startIndex={startIndex}
                endIndex={endIndex}
                sortBy={sortBy}
                setSortBy={setSortBy}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                handleInternshipClick={handleInternshipClick}
                toggleSaveListing={toggleSaveListing}
                isSaved={isSaved}
                compatMap={compatMap}
              />
            )}
         </>
       )}
     </View>

     <style jsx>{`
     @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

       @keyframes float {
         0% {
           transform: translateY(0px);
         }
         50% {
           transform: translateY(-30px);
         }
         100% {
           transform: translateY(0px);
         }
       }
       
       input[type="text"], 
       input[type="number"], 
       input[type="search"] {
         color: white !important;
         background-color: rgba(255,255,255,0.1) !important;
       }
       
       input::placeholder {
         color: rgba(255, 255, 255, 0.5) !important;
       }
       
       .amplify-card::-webkit-scrollbar {
         width: 8px;
       }
       
       .amplify-card::-webkit-scrollbar-track {
         background: rgba(255,255,255,0.05);
         border-radius: 4px;
       }
       
       .amplify-card::-webkit-scrollbar-thumb {
         background: rgba(102, 126, 234, 0.3);
         border-radius: 4px;
       }
       
       .amplify-card::-webkit-scrollbar-thumb:hover {
         background: rgba(102, 126, 234, 0.5);
       }
       
       .amplify-textfield__input {
         background-color: transparent !important;
         border: none !important;
       }
       
       .amplify-textfield {
         background-color: transparent !important;
       }
     `}</style>
   </View>
 );
};

export default InternshipListings;